import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber,
  getPSIOutputUnits,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Formatter for pressure values in PSI units.
 * Automatically converts negative pressure values to inches of mercury (inHG)
 * and positive values to PSIG (pounds per square inch gauge).
 *
 * @param {UnitValue} unitValue - The pressure value and units to format
 * @param {any} unitValue.value - The numeric pressure value
 * @param {string} [unitValue.units] - The current units of the value
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted numeric value
 *   - unitsLabel: 'PSIG' for positive values, 'inHG' for negative values
 *   - valueUnitsLabel: Combined value and units with proper spacing
 *
 * @example
 * formatPsi({ value: 15, units: 'PSI' })
 * // Returns: { valueLabel: '15', unitsLabel: 'PSIG', valueUnitsLabel: '15 PSIG' }
 *
 * @example
 * formatPsi({ value: -5, units: 'PSI' })
 * // Returns: { valueLabel: '10.18', unitsLabel: 'inHG', valueUnitsLabel: '10.18 inHG' }
 */
export const formatPsi = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const outputUnits = getPSIOutputUnits(unitValue, displayUnits);
  const transformed = transformUnits(unitValue, outputUnits);

  return unitValueToFormattedUnitLabels({
    value: formatNumber(transformed.value),
    units: transformed.units,
  });
};
